import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, from, map, of, switchMap, tap } from 'rxjs';
import { APP_CONSTANTS } from 'src/app/core/constants/enum.constants';
import { ApiService } from 'src/app/core/services/api.service';
import { getPlaceValue, getPrecision } from 'src/app/core/functions/utility';
import { Coin, CoinDetail } from 'src/app/core/models/coin.model';

@Injectable({
    providedIn: 'root'
})
export class CoinService {
    private _ixfiCoinsSubject = new BehaviorSubject<Coin[]>([]);
    ixfiCoinsObs = this._ixfiCoinsSubject.asObservable();

    private _coinDetailSubject = new BehaviorSubject<CoinDetail | null>(null);
    coinDetailObs = this._coinDetailSubject.asObservable();

    private _itbAssets: string[] = [];

    private apiService = inject(ApiService);

    fetchIxfiCoins(): Observable<Coin[]> {
        return this.apiService.fetch<Coin[]>('ALL_IXFI_COINS').pipe(
            map(response => {
                return response.map(item => {
                    item.change = 0;
                    if (!item.price) item.price = 0;
                    item.precision = getPrecision(item.price);
                    item.marketCapPlaceValue = getPlaceValue(item.market_cap);
                    item.kLineGraph = this.getKLineGraphSrc(item.coin_symbol, item.is_up);
                    return item;
                });
            }),
            tap(response => this._ixfiCoinsSubject.next(response))
        );
    }

    fetchCoinDetail(coinCodeInParams: string): Observable<CoinDetail> {
        const payload: { coinCode: string } = { coinCode: coinCodeInParams };
        return this.apiService.fetch<CoinDetail>('COIN_DETAIL', payload, coinCodeInParams).pipe(
            map(response => {
                const coinDetail: CoinDetail = response[0];
                coinDetail.precision = getPrecision(coinDetail.price);
                coinDetail.fixedPrecision = getPrecision(coinDetail.price);
                return coinDetail;
            }),
            tap(response => this._coinDetailSubject.next(response))
        );
    }

    getKLineGraphSrc(symbol: string, isUp: boolean): `coin-k-lines/${string}_${'green' | 'red'}_30d.svg` {
        const lowerCaseSymbol: string = symbol.toLowerCase();
        const redOrGreen: 'red' | 'green' = isUp ? 'green' : 'red';
        return `coin-k-lines/${lowerCaseSymbol}_${redOrGreen}_30d.svg`;
    }

    fetchItbAssets(): Observable<string[]> {
        const url: URL = new URL('https://api.intotheblock.com/assets');
        type ITBAsset = { name: string; symbol: string; id: number };

        const itbVendorKey: string = APP_CONSTANTS.ITB_API_KEY.toString();

        let result: Observable<string[]>;

        if (this._itbAssets.length === 0) {
            result = from(fetch(url, { headers: { 'X-Api-Key': itbVendorKey } }))
                .pipe(
                    switchMap(response => {
                        if (!response.ok) throw new Error(`HTTP Error! Status: ${response.status}`);
                        return response.json();
                    })
                )
                .pipe(
                    map((response: ITBAsset[]) => {
                        const symbols: string[] = response.map(asset => asset.symbol);
                        const uniqueSymbols: string[] = [...new Set(symbols)];
                        return uniqueSymbols;
                    }),
                    tap(response => (this._itbAssets = response))
                );
        } else result = of(this._itbAssets.slice());
        return result;
    }
    // check mobile view
    isWebview = () => {
        if (typeof window === undefined) {
            return false;
        }
        let navigator: any = window.navigator;
        const standalone = navigator.standalone;
        const userAgent = navigator.userAgent.toLowerCase();
        const safari = /safari/.test(userAgent);
        const ios = /iphone|ipod|ipad/.test(userAgent);
        return ios ? !standalone && !safari : userAgent.includes('wv');
    };
}
